import { Icon } from '../../../../widgets/icon';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, Suspense, useCallback, useEffect } from 'react';
import { T } from 'src/shared/helpers';
import ArrowLeftIcon from 'src/shared/material_icons/navigation/arrow_back.svg';
import ArrowRightIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import DownloadIcon from 'src/shared/material_icons/file/download.svg';
import TimesIcon from 'src/shared/material_icons/navigation/close.svg';
import styled from 'styled-components';
import { fadeIn, scaleIn } from '../../styles/animations';
import { downloadFile } from '../../utils/downloadHelpers';

const GlbViewer = React.lazy(() => import('../shared/glbViewer'));

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.9);
    animation: ${fadeIn} 0.2s ease;
`;

const ImageContainer = styled.div`
    position: relative;
    max-width: 90vw;
    max-height: 80vh;
    min-width: 0;
`;

const LightboxImage = styled.img`
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const LightboxVideo = styled.video`
    max-width: 90vw;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const LightboxModelContainer = styled.div`
    width: 70vw;
    height: 70vh;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    background: #f5f5f5;
`;

const CloseButton = styled.button`
    position: absolute;
    top: -48px;
    right: 0;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.25);
        transform: scale(1.1);
    }

    ${Icon} {
        width: 20px;
        height: 20px;
        min-width: 20px;
        fill: white;
    }
`;

const DownloadButton = styled.button`
    position: absolute;
    top: -48px;
    right: 56px;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.25);
        transform: scale(1.1);
    }

    ${Icon} {
        width: 20px;
        height: 20px;
        min-width: 20px;
        fill: white;
    }
`;

const LightboxRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    animation: ${scaleIn} 0.2s ease;
`;

const NavButton = styled.button`
    width: 48px;
    height: 48px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
    }

    ${Icon} {
        width: 20px;
        height: 20px;
        min-width: 20px;
        fill: white;
    }
`;

const NavPlaceholder = styled.div`
    width: 48px;
    flex-shrink: 0;
`;

const ImageInfo = styled.div`
    position: absolute;
    bottom: -40px;
    left: 0;
    right: 0;
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
    font-size: 13px;
`;

interface ImageLightboxProps {
    imageUrl: string;
    alt?: string;
    onClose: () => void;
    isVideo?: boolean;
    isModel?: boolean;
    showDownload?: boolean;
    onNext?: () => void;
    onPrevious?: () => void;
}

export const ImageLightbox: FC<ImageLightboxProps> = ({
    imageUrl,
    alt,
    onClose,
    isVideo = false,
    isModel = false,
    showDownload = true,
    onNext,
    onPrevious
}) => {
    // Handle escape key to close and arrow keys for navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            } else if (e.key === 'ArrowRight' && onNext) {
                onNext();
            } else if (e.key === 'ArrowLeft' && onPrevious) {
                onPrevious();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose, onNext, onPrevious]);

    // Prevent body scroll when lightbox is open
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    const handleOverlayClick = useCallback(
        (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        },
        [onClose]
    );

    const handleDownload = useCallback(async () => {
        const extension = isModel ? 'glb' : isVideo ? 'mp4' : 'png';
        try {
            await downloadFile(imageUrl, `ai-result.${extension}`);
        } catch (error) {
            console.error('Failed to download file:', error);
        }
    }, [imageUrl, isVideo, isModel]);

    return (
        <Overlay onClick={handleOverlayClick}>
            <LightboxRow>
                {onPrevious ? (
                    <NavButton onClick={onPrevious}>
                        <Icon>
                            <ArrowLeftIcon />
                        </Icon>
                    </NavButton>
                ) : (
                    onNext && <NavPlaceholder />
                )}

                <ImageContainer>
                    {showDownload && (
                        <ZTooltip position='bottom' title={T._('Download', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                            <DownloadButton onClick={handleDownload}>
                                <Icon>
                                    <DownloadIcon />
                                </Icon>
                            </DownloadButton>
                        </ZTooltip>
                    )}

                    <ZTooltip position='bottom' title={T._('Close', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                        <CloseButton onClick={onClose}>
                            <Icon>
                                <TimesIcon />
                            </Icon>
                        </CloseButton>
                    </ZTooltip>

                    {isModel ? (
                        <LightboxModelContainer>
                            <Suspense fallback={null}>
                                <GlbViewer url={imageUrl} />
                            </Suspense>
                        </LightboxModelContainer>
                    ) : isVideo ? (
                        <LightboxVideo src={imageUrl} controls autoPlay muted />
                    ) : (
                        <LightboxImage src={imageUrl} alt={alt || T._('Full size preview', 'Admin')} />
                    )}
                    {alt && <ImageInfo>{alt}</ImageInfo>}
                </ImageContainer>

                {onNext ? (
                    <NavButton onClick={onNext}>
                        <Icon>
                            <ArrowRightIcon />
                        </Icon>
                    </NavButton>
                ) : (
                    onPrevious && <NavPlaceholder />
                )}
            </LightboxRow>
        </Overlay>
    );
};

export default ImageLightbox;
